import React from "react";
import { getCsrfToken } from "../util";

const OrderContext = React.createContext(undefined);

export const OrderProvider = ({ children }) => {
  const [cart, setCart] = React.useState([]);
  const [currentOrder, setCurrentOrder] = React.useState(null);
  const [isProcessing, setIsProcessing] = React.useState(false);

  const addToCart = React.useCallback((item) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find((cartItem) => cartItem.id === item.id);
      if (existingItem) {
        return prevCart.map((cartItem) =>
          cartItem.id === item.id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem,
        );
      }
      return [...prevCart, { ...item, quantity: 1 }];
    });
  }, []);

  const removeFromCart = React.useCallback((itemId) => {
    setCart((prevCart) => prevCart.filter((item) => item.id !== itemId));
  }, []);

  const updateCartQuantity = React.useCallback(
    (itemId, quantity, itemPayload = null) => {
      if (quantity <= 0) {
        setCart((prevCart) => prevCart.filter((item) => item.id !== itemId));
        return;
      }
      setCart((prevCart) => {
        const existingItem = prevCart.find((item) => item.id === itemId);
        if (!existingItem) {
          if (!itemPayload) return prevCart;
          return [...prevCart, { ...itemPayload, quantity }];
        }

        return prevCart.map((item) =>
          item.id === itemId ? { ...item, quantity } : item,
        );
      });
    },
    [],
  );

  const clearCart = React.useCallback(() => {
    setCart([]);
  }, []);

  const getCartTotal = React.useCallback(() => {
    return cart.reduce(
      (total, item) => total + Number(item?.price?.amount || 0) * item.quantity,
      0,
    );
  }, [cart]);

  const getCartItemCount = React.useCallback(() => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  }, [cart]);

  const updateCartItemNote = React.useCallback((itemId, note) => {
    const normalizedNote = note.toString().slice(0, 200);
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.id === itemId ? { ...item, note: normalizedNote } : item,
      ),
    );
  }, []);

  const processPayment = React.useCallback(
    async ({
      userName,
      buildingCode,
      roomNumber,
      webPushSubscriptionId,
      email,
    }) => {
      setIsProcessing(true);
      try {
        const lines = cart.map((item) => ({
          orderable_type: item.orderableType || "FnbMenuItem",
          orderable_id: item.id,
          quantity: item.quantity,
          note: item.note || null,
        }));

        const orderPayload = {
          user_name: userName,
          building_code: buildingCode,
          room_number: roomNumber || null,
          email: email || null,
          lines,
        };
        if (webPushSubscriptionId) {
          orderPayload.web_push_subscription_id = webPushSubscriptionId;
        }

        const response = await fetch("/api/v1/orders", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": getCsrfToken(),
            Accept: "application/json",
          },
          body: JSON.stringify({ order: orderPayload }),
        });

        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.message || "Failed to create order");
        }

        const newOrder = payload?.data;
        setCurrentOrder(newOrder);
        setCart([]);
        return newOrder;
      } finally {
        setIsProcessing(false);
      }
    },
    [cart],
  );

  const resetOrder = React.useCallback(() => {
    setCurrentOrder(null);
  }, []);

  const value = React.useMemo(
    () => ({
      cart,
      currentOrder,
      isProcessing,
      addToCart,
      removeFromCart,
      updateCartQuantity,
      clearCart,
      getCartTotal,
      getCartItemCount,
      updateCartItemNote,
      processPayment,
      resetOrder,
    }),
    [
      cart,
      currentOrder,
      isProcessing,
      addToCart,
      removeFromCart,
      updateCartQuantity,
      clearCart,
      getCartTotal,
      getCartItemCount,
      updateCartItemNote,
      processPayment,
      resetOrder,
    ],
  );

  return (
    <OrderContext.Provider value={value}>{children}</OrderContext.Provider>
  );
};

export const useOrder = () => {
  const context = React.use(OrderContext);
  if (context === undefined) {
    throw new Error("useOrder must be used within an OrderProvider");
  }
  return context;
};
